// src/hooks/useMediaCapture.js - Hook personalizado para captura de medios
import { useState, useRef } from 'react';

export const useMediaCapture = () => {
  const [recording, setRecording] = useState(false);
  const [mediaBlob, setMediaBlob] = useState(null);
  const [mediaBlobUrl, setMediaBlobUrl] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [processingPhoto, setProcessingPhoto] = useState(false);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);

  // Marca de agua por defecto: nombre genérico + los 3 campos activados —
  // es el mismo comportamiento que ya tenía esta función antes de HU-005,
  // para no cambiar nada si algún llamador todavía no pasa `marcaAguaConfig`.
  const MARCA_AGUA_DEFECTO = {
    nombreEntidad: 'Cifra Negra',
    incluirFechaHora: true,
    incluirDireccion: true,
    incluirClima: true,
    incluirLogo: true,
  };

  const getWeatherIcon = (weatherCode, temperature) => {
    // Códigos WMO: https://open-meteo.com/en/docs
    if (weatherCode === 0) return '☀️';
    if (weatherCode >= 1 && weatherCode <= 3) return '⛅';
    if (weatherCode >= 45 && weatherCode <= 48) return '🌫️';
    if (weatherCode >= 51 && weatherCode <= 55) return '🌦️';
    if (weatherCode >= 56 && weatherCode <= 57) return '🌨️';
    if (weatherCode >= 61 && weatherCode <= 65) return '🌧️';
    if (weatherCode >= 66 && weatherCode <= 67) return '🌨️';
    if (weatherCode >= 71 && weatherCode <= 75) return '🌨️';
    if (weatherCode >= 77) return '🌨️';
    if (weatherCode >= 80 && weatherCode <= 82) return '🌦️';
    if (weatherCode >= 85 && weatherCode <= 86) return '🌨️';
    if (weatherCode >= 95) return '⛈️';
    if (weatherCode >= 96 && weatherCode <= 99) return '⛈️';
    // Sin código de clima real, se estima el ícono por temperatura — pero
    // nunca se llega acá sin una temperatura real (ver `addWatermark`).
    if (temperature >= 25) return '☀️';
    if (temperature >= 20) return '⛅';
    if (temperature >= 15) return '☁️';
    if (temperature >= 10) return '🌧️';
    return '❄️';
  };

  // Carga una imagen por URL como promesa — usado para el logo institucional
  // en la marca de agua. `crossOrigin` es necesario para poder leer el
  // canvas después (CORS_ALLOWED_ORIGINS ya incluye el origen real del
  // frontend, ver config/settings.py) — sin esto, un logo de otro origen
  // "mancha" el canvas y `canvas.toBlob()` falla para la FOTO ENTERA, no
  // solo el logo.
  const cargarImagen = (url) => new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });

  // Envuelve `texto` en como mucho `maxLines` líneas de hasta `maxWidth` px,
  // según la fuente ya seteada en `context` — la dirección de una foto puede
  // ser larga y no entra en una sola línea como el resto de campos. Si no
  // entra completo en `maxLines`, la última línea visible se corta con "…".
  const wrapText = (context, texto, maxWidth, maxLines) => {
    const palabras = texto.split(' ');
    const lineas = [];
    let lineaActual = '';

    for (const palabra of palabras) {
      const intento = lineaActual ? `${lineaActual} ${palabra}` : palabra;
      if (context.measureText(intento).width <= maxWidth || !lineaActual) {
        lineaActual = intento;
      } else {
        lineas.push(lineaActual);
        lineaActual = palabra;
        if (lineas.length === maxLines) break;
      }
    }
    if (lineaActual && lineas.length < maxLines) lineas.push(lineaActual);

    const textoCompleto = palabras.join(' ');
    const cabeCompleto = lineas.join(' ').length >= textoCompleto.length;
    if (!cabeCompleto) {
      const ultimaIdx = lineas.length - 1;
      let ultima = lineas[ultimaIdx];
      while (context.measureText(`${ultima}…`).width > maxWidth && ultima.length > 1) {
        ultima = ultima.slice(0, -1);
      }
      lineas[ultimaIdx] = `${ultima}…`;
    }
    return lineas;
  };

  /**
   * Marca de agua configurable (HU-005 Fase 3; rediseñada HU-008 Fase 4;
   * corregida post-cierre según referencia "Time Mark" del cliente) — estilo
   * app de timestamp: recuadro con borde punteado y velo oscuro liviano
   * (no una placa sólida) anclado en la esquina inferior IZQUIERDA de la
   * foto. Fila principal: hora en negrita grande + separador ámbar + fecha
   * y día de semana apilados — igual a la referencia. Debajo, dirección
   * (envuelta a 2 líneas si hace falta, según toggle `incluirDireccion`).
   *
   * Logo institucional (`Configuracion.logo`, editable en el panel): si
   * está configurado, reemplaza al nombre de la entidad como identificador
   * visual (se dibuja circular a la izquierda del texto) — sin logo, se
   * muestra el nombre de la entidad como línea de texto en su lugar. Si el
   * logo no carga (sin red, URL rota, etc.) la marca de agua sigue sin él,
   * nunca bloquea la foto.
   *
   * Bug real corregido en HU-005: antes, sin temperatura real disponible,
   * se generaba una al azar (`Math.random()`) — una foto de evidencia podía
   * terminar mostrando un clima inventado que parece real. Sin dato real
   * (`locationData.temperature == null`), esa línea simplemente no se dibuja.
   */
  const addWatermark = async (context, width, height, locationData, marcaAguaConfig = MARCA_AGUA_DEFECTO) => {
    const config = { ...MARCA_AGUA_DEFECTO, ...marcaAguaConfig };
    const now = new Date();
    // HU-014: antes se dibujaba apenas hubiera un logo cargado, sin forma de
    // desactivarlo — ahora respeta el toggle `incluirLogo` de Configuración.
    const tieneLogo = Boolean(config.logo) && config.incluirLogo;
    const mostrarFilaSuperior = config.incluirFechaHora;

    const margin = Math.max(width * 0.03, 14);
    const padding = Math.max(width * 0.028, 14);
    const logoSize = Math.max(width * 0.11, 46);
    const fontTime = Math.max(width * 0.075, 34);
    const fontSmall = Math.max(width * 0.026, 13);
    const filaSuperiorAlto = fontTime * 1.15;
    const lineHeight = fontSmall * 1.45;
    const radius = 10;
    const separacionDivisor = padding * 0.6;
    const grosorDivisor = Math.max(width * 0.004, 2);

    const anchoLogoBloque = tieneLogo ? logoSize + padding : 0;
    const anchoMaxTexto = Math.max(width - margin * 2 - padding * 2 - anchoLogoBloque, 60);

    // Líneas simples debajo de la fila hora/fecha: dirección primero (la más
    // relevante), después nombre de la entidad solo si no hay logo, y clima
    // al final si el toggle está activo y hay temperatura real.
    context.font = `500 ${fontSmall}px 'Segoe UI', Arial, sans-serif`;
    const lineasSimples = [];
    if (config.incluirDireccion && locationData?.address) {
      lineasSimples.push(...wrapText(context, locationData.address, anchoMaxTexto, 2));
    }
    if (!tieneLogo) lineasSimples.push(config.nombreEntidad);
    const tieneTemperaturaReal = locationData?.temperature != null;
    if (config.incluirClima && tieneTemperaturaReal) {
      const icono = getWeatherIcon(locationData.weatherCode, locationData.temperature);
      lineasSimples.push(`${icono} ${locationData.temperature}°C`);
    }

    if (!mostrarFilaSuperior && lineasSimples.length === 0 && !tieneLogo) return;

    const timeString = now.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit', hour12: false });
    const dateString = now.toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' });
    const weekdayString = now.toLocaleDateString('es-PE', { weekday: 'short' });

    context.font = `800 ${fontTime}px 'Segoe UI', Arial, sans-serif`;
    const anchoTime = context.measureText(timeString).width;
    context.font = `600 ${fontSmall}px 'Segoe UI', Arial, sans-serif`;
    const anchoFechaDia = Math.max(context.measureText(dateString).width, context.measureText(weekdayString).width);
    const anchoFilaSuperior = mostrarFilaSuperior ? anchoTime + separacionDivisor * 2 + anchoFechaDia : 0;

    context.font = `500 ${fontSmall}px 'Segoe UI', Arial, sans-serif`;
    const anchoTexto = Math.min(
      anchoMaxTexto,
      Math.max(anchoFilaSuperior, ...lineasSimples.map((l) => context.measureText(l).width), 1),
    );

    const boxWidth = Math.min(width - margin * 2, padding * 2 + anchoLogoBloque + anchoTexto);
    const boxHeight = padding * 2 + (mostrarFilaSuperior ? filaSuperiorAlto : 0) + lineasSimples.length * lineHeight;
    const boxX = margin; // esquina inferior izquierda, como en la referencia
    const boxY = height - margin - boxHeight;

    context.save();

    // Fondo: velo oscuro liviano + borde punteado (look "Time Mark" pedido
    // por el cliente) — reemplaza la placa sólida de la versión anterior.
    context.beginPath();
    context.moveTo(boxX + radius, boxY);
    context.arcTo(boxX + boxWidth, boxY, boxX + boxWidth, boxY + boxHeight, radius);
    context.arcTo(boxX + boxWidth, boxY + boxHeight, boxX, boxY + boxHeight, radius);
    context.arcTo(boxX, boxY + boxHeight, boxX, boxY, radius);
    context.arcTo(boxX, boxY, boxX + boxWidth, boxY, radius);
    context.closePath();
    context.fillStyle = 'rgba(0, 0, 0, 0.32)';
    context.fill();
    context.setLineDash([6, 4]);
    context.lineWidth = 1.5;
    context.strokeStyle = 'rgba(255, 255, 255, 0.85)';
    context.stroke();
    context.setLineDash([]);

    let textoX = boxX + padding;
    if (tieneLogo) {
      try {
        const img = await cargarImagen(config.logo);
        const cx = boxX + padding + logoSize / 2;
        const cy = boxY + boxHeight / 2;
        context.save();
        context.beginPath();
        context.arc(cx, cy, logoSize / 2, 0, Math.PI * 2);
        context.closePath();
        context.clip();
        context.drawImage(img, boxX + padding, cy - logoSize / 2, logoSize, logoSize);
        context.restore();
        textoX = boxX + padding * 2 + logoSize;
      } catch {
        // Sin red, URL rota, etc. — la marca de agua sigue sin el logo.
      }
    }

    context.textAlign = 'left';
    context.fillStyle = '#ffffff';
    let cursorY = boxY + padding;

    if (mostrarFilaSuperior) {
      context.font = `800 ${fontTime}px 'Segoe UI', Arial, sans-serif`;
      context.fillText(timeString, textoX, cursorY + fontTime * 0.82);

      const divisorX = textoX + anchoTime + separacionDivisor;
      context.strokeStyle = '#f5a623';
      context.lineWidth = grosorDivisor;
      context.beginPath();
      context.moveTo(divisorX, cursorY + fontTime * 0.1);
      context.lineTo(divisorX, cursorY + fontTime * 0.92);
      context.stroke();

      const fechaX = divisorX + separacionDivisor;
      context.font = `600 ${fontSmall}px 'Segoe UI', Arial, sans-serif`;
      context.fillText(dateString, fechaX, cursorY + fontSmall * 1.05);
      context.fillText(weekdayString, fechaX, cursorY + fontSmall * 1.05 + lineHeight);

      cursorY += filaSuperiorAlto;
    }

    context.font = `500 ${fontSmall}px 'Segoe UI', Arial, sans-serif`;
    lineasSimples.forEach((linea) => {
      cursorY += lineHeight;
      context.fillText(linea, textoX, cursorY - lineHeight * 0.3);
    });

    context.restore();
  };

  // Captura una foto desde la cámara y le dibuja la marca de agua encima
  // antes de convertirla a blob — `marcaAguaConfig` viene de `Configuracion`
  // de la instancia (HU-005 Fase 3), ver `addWatermark`.
  const capturePhoto = (webcamRef, mediaType, setError, locationData = null, marcaAguaConfig = undefined) => {
    // eslint-disable-next-line no-async-promise-executor -- ya se maneja
    // reject() explícito en el catch, no hay throw suelto sin capturar.
    return new Promise(async (resolve, reject) => {
      try {
        setProcessingPhoto(true);

        if (!webcamRef || !webcamRef.current) {
          setProcessingPhoto(false);
          reject(new Error('Webcam no disponible'));
          return;
        }

        const video = webcamRef.current;

        if (video.readyState < 2) {
          setProcessingPhoto(false);
          reject(new Error('Video no está listo para captura'));
          return;
        }

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        const width = video.videoWidth || video.clientWidth || 640;
        const height = video.videoHeight || video.clientHeight || 480;

        canvas.width = width;
        canvas.height = height;

        context.drawImage(video, 0, 0, width, height);
        await addWatermark(context, width, height, locationData, marcaAguaConfig);

        canvas.toBlob((blob) => {
          if (blob) {
            setMediaBlob(blob);
            setMediaBlobUrl(URL.createObjectURL(blob));
            setProcessingPhoto(false);
            resolve(true);
          } else {
            setProcessingPhoto(false);
            setError?.('Error creando la imagen');
            reject(new Error('Error creando la imagen'));
          }
        }, 'image/jpeg', 0.9);

      } catch (error) {
        console.error('Error capturando foto:', error);
        setProcessingPhoto(false);
        setError?.(error.message);
        reject(error);
      }
    });
  };

  // Función para iniciar grabación de video/audio
  const startRecording = (stream, mediaType, setError) => {
    return new Promise((resolve, reject) => {
      try {
        console.log('🎬 Intentando iniciar grabación...');
        console.log('📹 Stream:', stream);
        console.log('📹 MediaType:', mediaType);
        
        if (!stream) {
          console.error('❌ Stream no disponible');
          reject(new Error('Stream no disponible'));
          return;
        }

        // Limpiar estado anterior
        chunksRef.current = [];
        setRecordingTime(0);

        const options = {};
        if (mediaType === 'video') {
          // Priorizar formatos que resulten en archivos con extensión correcta
          if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')) {
            options.mimeType = 'video/webm;codecs=vp9,opus';
          } else if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')) {
            options.mimeType = 'video/webm;codecs=vp8,opus';
          } else if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported('video/webm')) {
            options.mimeType = 'video/webm';
          }
          // Sin esto, el navegador usa su propio bitrate por defecto (a
          // veces varios Mbps) — un video de pocos segundos ya pesaba
          // varios MB antes de tocar el límite de tamaño. 2.5 Mbps a 720p
          // (la resolución que ya pide `useCameraControls.js`) se ve bien
          // para evidencia, y reduce el peso real desde que se graba, no
          // después de subido (HU-008 Fase 7 — no es librería nueva, es la
          // propia API de `MediaRecorder`).
          options.videoBitsPerSecond = 2_500_000;
        } else {
          // Para audio, priorizar formatos que se puedan convertir fácilmente a MP3
          if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
            options.mimeType = 'audio/webm;codecs=opus';
          } else if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported('audio/webm')) {
            options.mimeType = 'audio/webm';
          } else if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
            options.mimeType = 'audio/ogg;codecs=opus';
          }
        }

        // Verificar si el navegador soporta el formato
        if (options.mimeType && typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && !MediaRecorder.isTypeSupported(options.mimeType)) {
          options.mimeType = mediaType === 'video' ? 'video/webm' : 'audio/webm';
        }

        // Si aún no es soportado, usar formato por defecto
        if (options.mimeType && typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && !MediaRecorder.isTypeSupported(options.mimeType)) {
          options.mimeType = '';
        }

        console.log('🎥 Iniciando grabación con opciones:', options);
        
        const mediaRecorder = new MediaRecorder(stream, options);
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            chunksRef.current.push(event.data);
            console.log('📦 Chunk recibido:', event.data.size, 'bytes');
          }
        };

        mediaRecorder.onstop = () => {
          console.log('🛑 Grabación detenida, procesando...');
          const fallbackType = mediaType === 'video' ? 'video/webm' : 'audio/webm';
          const blob = new Blob(chunksRef.current, { 
            type: options.mimeType || fallbackType
          });
          
          console.log('✅ Blob creado:', blob.size, 'bytes, tipo:', blob.type);
          
          setMediaBlob(blob);
          setMediaBlobUrl(URL.createObjectURL(blob));
          setRecording(false);
          setRecordingTime(0);
          
          // Limpiar intervalos
          if (recordingIntervalRef.current) {
            clearInterval(recordingIntervalRef.current);
          }
        };

        mediaRecorder.onstart = () => {
          console.log('🎬 Grabación iniciada');
          setRecording(true);
          setRecordingTime(0);
          
          // Iniciar contador de tiempo
          recordingIntervalRef.current = setInterval(() => {
            setRecordingTime(prev => prev + 1);
          }, 1000);
          
          // Resolver la promesa cuando la grabación inicie exitosamente
          resolve(true);
        };

        mediaRecorder.onerror = (event) => {
          console.error('❌ Error en grabación:', event.error);
          setError?.('Error durante la grabación: ' + event.error.message);
          setRecording(false);
          setRecordingTime(0);
          reject(new Error('Error durante la grabación: ' + event.error.message));
        };

        // Iniciar grabación
        mediaRecorder.start(1000); // Chunks cada segundo para mejor compatibilidad
        
      } catch (error) {
        console.error('❌ Error iniciando grabación:', error);
        setError?.(error.message);
        reject(error);
      }
    });
  };

  // Función para detener grabación
  const stopRecording = () => {
    try {
      if (mediaRecorderRef.current && recording) {
        console.log('🛑 Deteniendo grabación...');
        mediaRecorderRef.current.stop();
      }
    } catch (error) {
      console.error('❌ Error deteniendo grabación:', error);
    }
  };

  // Función para manejar selección de archivos
  const handleFileSelect = (file, mediaType) => {
    try {
      // Validar tipo de archivo
      const isValidType = validateFileType(file, mediaType);
      if (!isValidType) {
        return false;
      }

      // El tamaño será validado por el backend
      setSelectedFile(file);
      setMediaBlob(file);
      setMediaBlobUrl(URL.createObjectURL(file));
      
      return true;
    } catch (error) {
      console.error('❌ Error procesando archivo:', error);
      return false;
    }
  };

  // Función para validar tipo de archivo
  const validateFileType = (file, mediaType) => {
    const validTypes = {
      photo: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
      video: ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'],
      audio: ['audio/mp3', 'audio/wav', 'audio/ogg', 'audio/m4a', 'audio/aac'],
      file: ['application/pdf', 'application/msword', 'text/plain', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
    };

    const allowedTypes = validTypes[mediaType] || [];
    return allowedTypes.includes(file.type) || file.type.startsWith('image/') || file.type.startsWith('video/') || file.type.startsWith('audio/');
  };

  // Función para resetear captura
  const resetCapture = () => {
    setRecording(false);
    setMediaBlob(null);
    setSelectedFile(null);
    setRecordingTime(0);
    setProcessingPhoto(false);
    
    // Limpiar URL del blob anterior
    if (mediaBlobUrl) {
      URL.revokeObjectURL(mediaBlobUrl);
      setMediaBlobUrl(null);
    }
    
    // Limpiar intervalos
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
    }
    
    // Limpiar referencias
    chunksRef.current = [];
    mediaRecorderRef.current = null;
  };

  // Función para formatear tiempo de grabación
  const formatRecordingTime = () => {
    const hours = Math.floor(recordingTime / 3600);
    const minutes = Math.floor((recordingTime % 3600) / 60);
    const seconds = recordingTime % 60;
    return `REC ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  return {
    recording,
    mediaBlob,
    mediaBlobUrl,
    recordingTime,
    selectedFile,
    processingPhoto,
    capturePhoto,
    startRecording,
    stopRecording,
    handleFileSelect,
    resetCapture,
    formatRecordingTime
  };
};

